import React from 'react';
import { Square, Home, Bath, TreePine, Car, Dice4, Building, Check, Map, Phone, Mail } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import ProfileImage from '../property/ProfileImage'; // Importamos el componente actualizado
import Modal from '../property/Modal';

// URLs de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Componente para imágenes de propiedades
const PropertyImage = ({ url, alt, className }) => {
  if (!url) return (
    <img
      src="/default-property-image.jpg"
      alt={alt}
      className={className}
    />
  );

  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        console.error('Error loading property image:', fullUrl);
        e.target.src = '/default-property-image.jpg';
      }}
    />
  );
};

const LuxuryHomeListing = ({ property }) => {
  // Asegurar que siempre tengamos un objeto vendedor válido
  const vendedor = property?.vendedor || {
    nombre: 'KAIROS INMOBILIARIA',
    foto_perfil: null,
    tipo_usuario_id: 1,
    email_verificado: false,
    telefono_verificado: false,
    ubicacion: 'PIURA, PERÚ',
    sobre_mi: '',
    sobre_nosotros: '',
    whatsapp: '',
    telefono: '',
    facebook: '',
    instagram: '',
    nombre_usuario: ''
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE').format(price);
  };

  // Generate property URL
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = property.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${property.id}`;
  };

  const tipoUsuario = {
    1: 'Cliente',
    2: 'Vendedor',
    3: 'Constructora',
    4: 'Inmobiliaria'
  }[vendedor.tipo_usuario_id] || 'Usuario';

  const features = [
    { icon: Home, label: `${property.habitaciones} Dormitorios` },
    { icon: Bath, label: `${property.banos} Baños` },
    { icon: Square, label: `${property.area_total}m²` },
    { icon: Car, label: 'Estacionamiento' }
  ];

  const formatTitle = (title) => {
    if (!title) return [''];
    return [title]; // Devuelve el título completo en una sola línea
  };

  const titleLines = formatTitle(property.titulo);

  return (
    <div className="max-w-4xl mx-auto bg-gradient-to-b from-gray-900 to-gray-800 text-white overflow-hidden rounded-lg relative shadow-2xl">
      {/* Top Accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-500 via-teal-400 to-teal-600"></div>
      
      <div className="p-8">
        {/* Header with Seller Info and Property Type */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <div className="relative">
              <ProfileImage 
                url={vendedor.foto_perfil}
                alt={vendedor.nombre}
                className="w-16 h-16 rounded-full object-cover border-2 border-teal-400"
              />
              {(vendedor.email_verificado && vendedor.telefono_verificado) && (
                <div className="absolute -bottom-1 -right-1 bg-teal-500 rounded-full p-1">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            <div>
              <div className="text-white font-semibold text-lg">
                {vendedor.nombre || 'KAIROS INMOBILIARIA'}
              </div>
              <div className="text-sm text-gray-300 flex items-center gap-2">
                <Building className="w-4 h-4 text-teal-400" />
                {tipoUsuario}
              </div>
            </div>
          </div>
          
          {/* Property Status Badge */}
          <div className="bg-teal-500/20 border border-teal-500 rounded-full px-4 py-1 text-teal-400 font-medium">
            {property.operacion === 'alquiler' ? 'EN ALQUILER' : 'EN VENTA'}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col md:flex-row gap-8">
          {/* Left Content */}
          <div className="w-full md:w-1/2">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4 max-w-md">
                {property.titulo}
              </h1>
            </div>

            <div className="flex justify-between items-end mb-8">
              <div>
                <div className="text-teal-400 text-sm uppercase tracking-wider mb-1">Precio desde</div>
                <div className="text-2xl font-bold">S/ {formatPrice(property.precio_soles)}</div>
                <div className="text-gray-400 text-sm">USD {formatPrice(property.precio_dolares)}</div>
              </div>
              
              {/* QR Code */}
              <div className="bg-white p-2 rounded-lg">
              <div></div>
                <QRCodeSVG value={getPropertyUrl()} size={90} />
              </div>
            </div>

            {/* Features */}
            <div className="mb-6">
              <h2 className="text-teal-400 text-lg font-medium mb-3 flex items-center gap-2 border-b border-gray-700 pb-2">
                <Square className="w-5 h-5" /> Características
              </h2>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-4">
                {features.map((Feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-800/50 p-2 rounded-md">
                    <Feature.icon className="w-5 h-5 text-teal-400" />
                    <span className="text-sm">{Feature.label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Property Description */}
            <div>
              <h2 className="text-teal-400 text-lg font-medium mb-3 flex items-center gap-2 border-b border-gray-700 pb-2">
                <Home className="w-5 h-5" /> Descubre tu Próximo Hogar
              </h2>
              <p className="text-gray-300 mt-4 mb-6">
              {property.descripcion_corta || 'Diseño de alta calidad y acabados de lujo en una ubicación privilegiada.'}
            </p>
            </div>
          </div>

          {/* Right Images Section - Corregido */}
          <div className="w-full md:w-1/2">
            <div className="flex flex-col gap-4">
              <div className="relative overflow-hidden rounded-lg shadow-lg h-64">
                <PropertyImage 
                  url={property.imagenes[0] || ''}
                  alt="Vista principal"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/70 via-transparent to-transparent"></div>
              </div>
              <div className="relative overflow-hidden rounded-lg shadow-lg h-60">
                <PropertyImage 
                  url={property.imagenes[1] || ''}
                  alt="Vista secundaria"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative overflow-hidden rounded-lg shadow-lg h-60">
                <PropertyImage 
                  url={property.imagenes[2] || ''}
                  alt="Vista adicional"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Contact Section */}
      <div className="mt-8 bg-gradient-to-r from-teal-800 to-teal-900 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-teal-300" />
            <div>
              <div className="text-sm text-teal-300">Llámanos</div>
              <div className="font-bold">{property.telefono || vendedor.telefono || '+51 123 456 789'}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-teal-300" />
            <div>
              <div className="text-sm text-teal-300">Email</div>
              <div>{property.email || vendedor.email || 'contacto@inkayros.com'}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Map className="w-5 h-5 text-teal-300" />
            <div>
              <div className="text-sm text-teal-300">Ubicación</div>
              <div>{property.ubicacion}</div>
            </div>
          </div>
        </div>
        
        {/* Website URL */}
        <div className="mt-4 text-center border-t border-teal-700/50 pt-4">
          <div className="font-medium text-teal-300">{getPropertyUrl()}</div>
        </div>
      </div>
    </div>
  );
};

// Preview component
export const LuxuryHomeListingPreview = ({ isOpen, onClose, property }) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-4xl mx-auto">
        <LuxuryHomeListing property={property} />
        <button
          onClick={onClose}
          className="mt-6 w-full bg-gradient-to-r from-teal-700 to-teal-900 
                   text-white py-4 px-6 rounded-xl font-semibold 
                   shadow-lg hover:shadow-xl transition-all duration-300 
                   hover:scale-105 active:scale-100"
        >
          Cerrar
        </button>
      </div>
    </Modal>
  );
};

export default LuxuryHomeListing;